import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import styles from './AdaptiveProgress.module.css';

interface AdaptiveProgressProps {
  isProcessing: boolean;
  startTime?: number;
  estimatedDuration?: number; // in milliseconds, default 30000 (30 seconds)
  onComplete?: () => void;
  size?: 'sm' | 'md' | 'lg';
  showPercentage?: boolean;
  showMessages?: boolean;
  className?: string;
}

const AdaptiveProgress: React.FC<AdaptiveProgressProps> = ({
  isProcessing,
  startTime,
  estimatedDuration = 30000, // 30 seconds default
  onComplete,
  size = 'lg',
  showPercentage = true,
  showMessages = true,
  className = ''
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const animationRef = useRef<number>();
  const progressRef = useRef(0);
  const startTimeRef = useRef<number>(Date.now());
  const adaptiveSpeedRef = useRef(1);

  // Messages to cycle through
  const messages = [
    'upload.loading.processing',
    'upload.loading.extracting',
    'upload.loading.analyzing',
    'upload.loading.mapping',
    'upload.loading.schema',
    'upload.loading.validating'
  ];

  // Size configurations
  const sizeConfig = {
    sm: { size: 80, strokeWidth: 4, fontSize: '0.875rem' },
    md: { size: 120, strokeWidth: 6, fontSize: '1rem' },
    lg: { size: 180, strokeWidth: 8, fontSize: '1.25rem' }
  };

  const config = sizeConfig[size];
  const radius = (config.size - config.strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  
  console.log('🎨 AdaptiveProgress render:', { progress, displayProgress, isProcessing });

  useEffect(() => {
    console.log('🔄 AdaptiveProgress useEffect triggered:', { isProcessing, startTime, estimatedDuration });
    
    if (isProcessing) {
      startTimeRef.current = startTime || Date.now();
      progressRef.current = 0;
      setProgress(0);
      setDisplayProgress(0);
      setMessageIndex(0);
      adaptiveSpeedRef.current = 1;

      console.log('🚀 Starting animation loop at:', new Date(startTimeRef.current).toISOString());

      // Start the animation loop
      const animate = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const baseProgress = (elapsed / estimatedDuration) * 100;
        
        console.log('📊 Animation frame:', { elapsed, baseProgress, estimatedDuration });

        // Adaptive progress calculation
        if (baseProgress < 80) {
          // Normal speed for first 80%
          progressRef.current = baseProgress;
        } else if (baseProgress < 95) {
          // Slow down significantly between 80-95%
          const slowdownFactor = 0.3;
          const additionalProgress = (baseProgress - 80) * slowdownFactor;
          progressRef.current = 80 + additionalProgress;
        } else {
          // Very slow progress after 95%
          const crawlFactor = 0.1;
          const additionalProgress = (baseProgress - 95) * crawlFactor;
          progressRef.current = Math.min(95 + additionalProgress, 99);
        }

        // Smooth easing for display
        const easedProgress = easeInOutCubic(progressRef.current / 100) * 100;
        setProgress(progressRef.current);
        setDisplayProgress(easedProgress);

        console.log('📈 Progress updated:', { 
          progressRef: progressRef.current, 
          easedProgress, 
          isProcessing,
          willContinue: isProcessing && progressRef.current < 99 
        });

        // Continue animation if still processing
        if (isProcessing && progressRef.current < 99) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          console.log('🛑 Animation stopped:', { isProcessing, progress: progressRef.current });
        }
      };

      animationRef.current = requestAnimationFrame(animate);

      // Message rotation
      const messageInterval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, 3000);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        clearInterval(messageInterval);
      };
    } else if (progress > 0) {
      // Rush to complete when processing finishes
      const rushToComplete = () => {
        const targetProgress = 100;
        const currentProgress = progressRef.current;
        const rushDuration = 500; // 500ms to complete
        const rushStartTime = Date.now();

        const rushAnimate = () => {
          const rushElapsed = Date.now() - rushStartTime;
          const rushProgress = Math.min(rushElapsed / rushDuration, 1);
          
          progressRef.current = currentProgress + (targetProgress - currentProgress) * easeOutQuart(rushProgress);
          const easedProgress = progressRef.current;
          
          setProgress(progressRef.current);
          setDisplayProgress(easedProgress);

          if (rushProgress < 1) {
            animationRef.current = requestAnimationFrame(rushAnimate);
          } else {
            if (onComplete) {
              setTimeout(onComplete, 200);
            }
          }
        };

        animationRef.current = requestAnimationFrame(rushAnimate);
      };

      rushToComplete();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isProcessing, estimatedDuration, onComplete, startTime]);

  // Easing functions
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  };

  const easeOutQuart = (t: number): number => {
    return 1 - Math.pow(1 - t, 4);
  };

  // Calculate stroke dash offset for circular progress
  const strokeDashoffset = circumference - (displayProgress / 100) * circumference;

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <div className={`relative ${styles.progressContainer}`}>
        <svg
          width={config.size}
          height={config.size}
          className={styles.progressSvg}
        >
          {/* Background circle */}
          <circle
            cx={config.size / 2}
            cy={config.size / 2}
            r={radius}
            strokeWidth={config.strokeWidth}
            className={styles.progressBackground}
          />
          
          {/* Progress circle */}
          <circle
            cx={config.size / 2}
            cy={config.size / 2}
            r={radius}
            strokeWidth={config.strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={styles.progressCircle}
            style={{
              transform: `rotate(-90deg)`,
              transformOrigin: '50% 50%',
              transition: 'stroke-dashoffset 0.3s ease-out'
            }}
          />
        </svg>

        {/* Center content */}
        <div className={styles.progressContent}>
          {showPercentage && (
            <div 
              className={styles.progressPercentage} 
              style={{ 
                fontSize: config.fontSize,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                padding: '4px 8px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              {Math.round(displayProgress)}%
            </div>
          )}
        </div>
      </div>

      {/* Loading message */}
      {showMessages && (
        <p className={`text-primary text-lg ${styles.progressMessage}`}>
          {t(messages[messageIndex])}
        </p>
      )}
    </div>
  );
};

export default AdaptiveProgress;